import { Fetcher } from '@this/src/util';
import type { PaymentMethodOptions, PaymentGateway, PaymentGatewayMember, PaymentInfo } from '../types/payment';

export type { PaymentGatewayMember, PaymentInfo };

type SelectOptionResponse = {
  id: number;
  name: string;
};

type DepartmentOptionResponse = {
  id: number;
  name: string;
  organization_address: {
    id: number | null;
    postal_code: string | null;
    location: string | null;
    addressee: string | null;
  } | null;
};

type OrganizationBaseOptionResponse = {
  id: number;
  name: string | null;
  postal_code: string | null;
  address: string | null;
  addressee: string | null;
};

type ExicCardOptionResponse = {
  id: number;
  exic_number: string | null;
};
type TravelerCurrentValuesResponse = {
  flight_first_name: string | null;
  flight_last_name: string | null;
  flight_first_name_kana: string | null;
  flight_last_name_kana: string | null;
  flight_first_name_roman: string | null;
  flight_last_name_roman: string | null;
  flight_birthday: string | null;
  flight_gender: string | null;
  flight_tel: string | null;
  air_seat: string | null;
  mileage_number: string | null;
  passport_number: string | null;
  passport_expire: string | null;
  passport_issued_country: string | null;
};

type TravelerFieldsResponse = {
  required: string[];
  optional: string[];
  current_values: TravelerCurrentValuesResponse;
};

type ReservationInfoResponse = {
  current_values: {
    final_destination: string | null;
    purpose: string | null;
    internal_number: string | null;
    charging_department_id: number | null;
    project_id: number | null;
  };
  setting_flags: {
    show_destination_business_trip: boolean;
    is_destination_business_trip_required: boolean;
    show_purpose_business_trip: boolean;
    is_purpose_business_trip_required: boolean;
    show_internal_number: boolean;
    is_internal_number_required: boolean;
    show_charging_department: boolean;
    is_charging_department_required: boolean;
    show_project_name: boolean;
    is_project_name_required: boolean;
  };
  approval_required: boolean;
  options: {
    departments: DepartmentOptionResponse[];
    organization_bases: OrganizationBaseOptionResponse[];
    exic_cards: ExicCardOptionResponse[];
    projects: SelectOptionResponse[];
  };
  selected_categories: string[];
  transport_types: {
    outward: 'air' | 'rail' | null;
    homeward: 'air' | 'rail' | null;
    has_air: boolean;
    has_rail: boolean;
  };
  shinkansen_info: {
    current_values: {
      shinkansen_address_type: 'select' | 'text' | 'organization_base_select' | null;
      postcode: string | null;
      shinkansen_address: string | null;
      shinkansen_addressee: string | null;
      shinkansen_ticket_type: 'normal' | 'kyuusyuu' | 'bulk_ticket' | 'ex' | null;
      shinkansen_seat_type: 'free' | 'reserve' | 'green' | null;
      shinkansen_seat: string | null;
      exic_number: string | null;
      exic_card_id: number | null;
      preferred_ticketing_method: 'qr' | 'postal' | null;
    };
    settings: {
      use_qr: boolean;
      use_postal: boolean;
      enabled_customize_address: boolean;
      show_ex: boolean;
      use_kyuusyuu_ticket: boolean;
      use_bulk_ticket: boolean;
      exic_usage_mode: 'user_based' | 'card_based';
      allow_user_exic_input: boolean;
      has_departments: boolean;
      has_organization_bases: boolean;
      organization_base_select_required: boolean;
    };
  } | null;
  traveler_fields: TravelerFieldsResponse | null;
  hotel_fields: TravelerFieldsResponse | null;
  payment: {
    payment_method_options: PaymentMethodOptions;
    gateway_type: PaymentGateway;
    gateway_members: PaymentGatewayMember[];
    gmo_shop_id: string;
    gmo_token_host: string;
  } | null;
  passport_minimum_expire_on: string | null;
};

export type SelectOption = {
  id: number;
  name: string;
};

export type DepartmentOption = {
  id: number;
  name: string;
  organizationAddressId: number | null;
  postalCode: string;
  address: string;
  addressee: string;
};

export type OrganizationBaseOption = {
  id: number;
  name: string;
  postalCode: string;
  address: string;
  addressee: string;
};

export type ExicCardOption = {
  id: number;
  exicNumber: string;
};

export type TravelerCurrentValues = {
  flightFirstName: string | null;
  flightLastName: string | null;
  flightFirstNameKana: string | null;
  flightLastNameKana: string | null;
  flightFirstNameRoman: string | null;
  flightLastNameRoman: string | null;
  flightBirthday: string | null;
  flightGender: string | null;
  flightTel: string | null;
  airSeat: string | null;
  mileageNumber: string | null;
  passportNumber: string | null;
  passportExpire: string | null;
  passportIssuedCountry: string | null;
};

export type TravelerFormValues = {
  flightFirstName: string;
  flightLastName: string;
  flightFirstNameKana: string;
  flightLastNameKana: string;
  flightFirstNameRoman: string;
  flightLastNameRoman: string;
  flightBirthday: string;
  flightGender: string;
  flightTel: string;
  airSeat: string;
  mileageNumber: string;
  passportNumber: string;
  passportExpire: string;
  passportIssuedCountry: string;
  shinkansenTicketType?: 'normal' | 'kyuusyuu' | 'bulk_ticket' | 'ex' | '';
  shinkansenSeatType?: 'free' | 'reserve' | 'green' | '';
  shinkansenSeat?: string;
  exicNumber?: string;
  exicPassword?: string;
  exicCardId?: number | null;
};

export type TravelerFields = {
  required: string[];
  optional: string[];
  currentValues: TravelerCurrentValues;
};

export const isForeignFlightTravelerFields = (fields: TravelerFields | null): boolean =>
  fields?.required.includes('flight_first_name_roman') ?? false;

export type ReservationInfo = {
  currentValues: {
    finalDestination: string | null;
    purpose: string | null;
    internalNumber: string | null;
    chargingDepartmentId: number | null;
    projectId: number | null;
  };
  settingFlags: {
    showDestinationBusinessTrip: boolean;
    isDestinationBusinessTripRequired: boolean;
    showPurposeBusinessTrip: boolean;
    isPurposeBusinessTripRequired: boolean;
    showInternalNumber: boolean;
    isInternalNumberRequired: boolean;
    showChargingDepartment: boolean;
    isChargingDepartmentRequired: boolean;
    showProjectName: boolean;
    isProjectNameRequired: boolean;
  };
  approvalRequired: boolean;
  options: {
    departments: DepartmentOption[];
    organizationBases: OrganizationBaseOption[];
    exicCards: ExicCardOption[];
    projects: SelectOption[];
  };
  selectedCategories: string[];
  transportTypes: {
    outward: 'air' | 'rail' | null;
    homeward: 'air' | 'rail' | null;
    hasAir: boolean;
    hasRail: boolean;
  };
  shinkansenInfo: {
    currentValues: {
      shinkansenAddressType: 'select' | 'text' | 'organization_base_select' | null;
      postcode: string | null;
      shinkansenAddress: string | null;
      shinkansenAddressee: string | null;
      shinkansenTicketType: 'normal' | 'kyuusyuu' | 'bulk_ticket' | 'ex' | null;
      shinkansenSeatType: 'free' | 'reserve' | 'green' | null;
      shinkansenSeat: string | null;
      exicNumber: string | null;
      exicCardId: number | null;
      preferredTicketingMethod: 'qr' | 'postal' | null;
    };
    settings: {
      useQr: boolean;
      usePostal: boolean;
      enabledCustomizeAddress: boolean;
      showEx: boolean;
      useKyuusyuuTicket: boolean;
      useBulkTicket: boolean;
      exicUsageMode: 'user_based' | 'card_based';
      allowUserExicInput: boolean;
      hasDepartments: boolean;
      hasOrganizationBases: boolean;
      organizationBaseSelectRequired: boolean;
    };
  } | null;
  travelerFields: TravelerFields | null;
  hotelFields: TravelerFields | null;
  payment: PaymentInfo | null;
  passportMinimumExpireOn: string | null;
};

const convertTravelerCurrentValues = (response: TravelerCurrentValuesResponse): TravelerCurrentValues => ({
  flightFirstName: response.flight_first_name,
  flightLastName: response.flight_last_name,
  flightFirstNameKana: response.flight_first_name_kana,
  flightLastNameKana: response.flight_last_name_kana,
  flightFirstNameRoman: response.flight_first_name_roman,
  flightLastNameRoman: response.flight_last_name_roman,
  flightBirthday: response.flight_birthday,
  flightGender: response.flight_gender,
  flightTel: response.flight_tel,
  airSeat: response.air_seat,
  mileageNumber: response.mileage_number,
  passportNumber: response.passport_number,
  passportExpire: response.passport_expire,
  passportIssuedCountry: response.passport_issued_country
});

const convertTravelerFields = (response: TravelerFieldsResponse | null): TravelerFields | null => {
  if (!response) return null;
  return {
    required: response.required,
    optional: response.optional,
    currentValues: convertTravelerCurrentValues(response.current_values)
  };
};

const convertDepartmentOption = (response: DepartmentOptionResponse): DepartmentOption => ({
  id: response.id,
  name: response.name,
  organizationAddressId: response.organization_address?.id || null,
  postalCode: response.organization_address?.postal_code || '',
  address: response.organization_address?.location || '',
  addressee: response.organization_address?.addressee || ''
});

const convertOrganizationBaseOption = (response: OrganizationBaseOptionResponse): OrganizationBaseOption => ({
  id: response.id,
  name: response.name || '',
  postalCode: response.postal_code || '',
  address: response.address || '',
  addressee: response.addressee || ''
});

const convertExicCardOption = (response: ExicCardOptionResponse): ExicCardOption => ({
  id: response.id,
  exicNumber: response.exic_number || ''
});

const convertResponse = (response: ReservationInfoResponse): ReservationInfo => ({
  currentValues: {
    finalDestination: response.current_values.final_destination,
    purpose: response.current_values.purpose,
    internalNumber: response.current_values.internal_number,
    chargingDepartmentId: response.current_values.charging_department_id,
    projectId: response.current_values.project_id
  },
  settingFlags: {
    showDestinationBusinessTrip: response.setting_flags.show_destination_business_trip,
    isDestinationBusinessTripRequired: response.setting_flags.is_destination_business_trip_required,
    showPurposeBusinessTrip: response.setting_flags.show_purpose_business_trip,
    isPurposeBusinessTripRequired: response.setting_flags.is_purpose_business_trip_required,
    showInternalNumber: response.setting_flags.show_internal_number,
    isInternalNumberRequired: response.setting_flags.is_internal_number_required,
    showChargingDepartment: response.setting_flags.show_charging_department,
    isChargingDepartmentRequired: response.setting_flags.is_charging_department_required,
    showProjectName: response.setting_flags.show_project_name,
    isProjectNameRequired: response.setting_flags.is_project_name_required
  },
  approvalRequired: response.approval_required,
  options: {
    departments: response.options.departments.map(convertDepartmentOption),
    organizationBases: response.options.organization_bases.map(convertOrganizationBaseOption),
    exicCards: response.options.exic_cards.map(convertExicCardOption),
    projects: response.options.projects
  },
  selectedCategories: response.selected_categories,
  transportTypes: {
    outward: response.transport_types.outward,
    homeward: response.transport_types.homeward,
    hasAir: response.transport_types.has_air,
    hasRail: response.transport_types.has_rail
  },
  shinkansenInfo: response.shinkansen_info
    ? {
        currentValues: {
          shinkansenAddressType: response.shinkansen_info.current_values.shinkansen_address_type,
          postcode: response.shinkansen_info.current_values.postcode,
          shinkansenAddress: response.shinkansen_info.current_values.shinkansen_address,
          shinkansenAddressee: response.shinkansen_info.current_values.shinkansen_addressee,
          shinkansenTicketType: response.shinkansen_info.current_values.shinkansen_ticket_type,
          shinkansenSeatType: response.shinkansen_info.current_values.shinkansen_seat_type,
          shinkansenSeat: response.shinkansen_info.current_values.shinkansen_seat,
          exicNumber: response.shinkansen_info.current_values.exic_number,
          exicCardId: response.shinkansen_info.current_values.exic_card_id,
          preferredTicketingMethod: response.shinkansen_info.current_values.preferred_ticketing_method
        },
        settings: {
          useQr: response.shinkansen_info.settings.use_qr,
          usePostal: response.shinkansen_info.settings.use_postal,
          enabledCustomizeAddress: response.shinkansen_info.settings.enabled_customize_address,
          showEx: response.shinkansen_info.settings.show_ex,
          useKyuusyuuTicket: response.shinkansen_info.settings.use_kyuusyuu_ticket,
          useBulkTicket: response.shinkansen_info.settings.use_bulk_ticket,
          exicUsageMode: response.shinkansen_info.settings.exic_usage_mode,
          allowUserExicInput: response.shinkansen_info.settings.allow_user_exic_input,
          hasDepartments: response.shinkansen_info.settings.has_departments,
          hasOrganizationBases: response.shinkansen_info.settings.has_organization_bases,
          organizationBaseSelectRequired: response.shinkansen_info.settings.organization_base_select_required
        }
      }
    : null,
  travelerFields: convertTravelerFields(response.traveler_fields),
  hotelFields: convertTravelerFields(response.hotel_fields),
  payment: response.payment
    ? {
        paymentMethodOptions: response.payment.payment_method_options,
        gatewayType: response.payment.gateway_type,
        gatewayMembers: response.payment.gateway_members,
        gmoShopId: response.payment.gmo_shop_id,
        gmoTokenHost: response.payment.gmo_token_host
      }
    : null,
  passportMinimumExpireOn: response.passport_minimum_expire_on
});

export const fetchReservationInfo = async (tripId: string): Promise<ReservationInfo> => {
  const response = await Fetcher.get<ReservationInfoResponse>(`/trips/${tripId}/ai_reservation_info`);
  return convertResponse(response);
};

export type SaveReservationInfoParams = {
  finalDestination?: string;
  purpose?: string;
  internalNumber?: string;
  chargingDepartmentId?: number | null;
  projectId?: number | null;
  organizationAddressId?: number | null;
  shinkansenAddressType?: 'select' | 'text' | 'organization_base_select';
  postcode?: string;
  shinkansenAddress?: string;
  shinkansenAddressee?: string;
  preferredTicketingMethod?: 'qr' | 'postal';
  traveler?: TravelerFormValues | null;
  foreignFlightTraveler?: boolean;
};

type TravelerSubmitParams = {
  flight_first_name: string;
  flight_last_name: string;
  flight_first_name_kana: string;
  flight_last_name_kana: string;
  flight_first_name_roman: string;
  flight_last_name_roman: string;
  flight_birthday: string;
  flight_gender: string;
  flight_tel: string;
  air_seat: string;
  mileage_number: string;
  passport_number: string;
  passport_expire: string;
  passport_issued_country: string;
  shinkansen_ticket_type?: string;
  shinkansen_seat_type?: string;
  shinkansen_seat?: string;
  exic_number?: string;
  exic_password?: string;
  exic_card_id?: number | null;
};

type SaveReservationInfoSubmitParams = {
  final_destination?: string;
  purpose?: string;
  internal_number?: string;
  charging_department_id?: number | null;
  project_id?: number | null;
  organization_address_id?: number | null;
  shinkansen_address_type?: 'select' | 'text' | 'organization_base_select';
  postcode?: string;
  shinkansen_address?: string;
  shinkansen_addressee?: string;
  preferred_ticketing_method?: 'qr' | 'postal';
  traveler?: TravelerSubmitParams;
};

type SaveReservationInfoResponse = {
  success: boolean;
  error?: string;
};

const convertTravelerToSubmitParams = (
  traveler: TravelerFormValues,
  foreignFlightTraveler: boolean
): TravelerSubmitParams => ({
  flight_first_name: foreignFlightTraveler ? traveler.flightFirstNameRoman : traveler.flightFirstName,
  flight_last_name: foreignFlightTraveler ? traveler.flightLastNameRoman : traveler.flightLastName,
  flight_first_name_kana: traveler.flightFirstNameKana,
  flight_last_name_kana: traveler.flightLastNameKana,
  flight_first_name_roman: traveler.flightFirstNameRoman,
  flight_last_name_roman: traveler.flightLastNameRoman,
  flight_birthday: traveler.flightBirthday,
  flight_gender: traveler.flightGender,
  flight_tel: traveler.flightTel,
  air_seat: traveler.airSeat,
  mileage_number: traveler.mileageNumber,
  passport_number: traveler.passportNumber,
  passport_expire: traveler.passportExpire,
  passport_issued_country: traveler.passportIssuedCountry,
  shinkansen_ticket_type: traveler.shinkansenTicketType,
  shinkansen_seat_type: traveler.shinkansenSeatType,
  shinkansen_seat: traveler.shinkansenSeat,
  exic_number: traveler.exicNumber,
  exic_password: traveler.exicPassword,
  exic_card_id: traveler.exicCardId
});

const convertToSubmitParams = (params: SaveReservationInfoParams): SaveReservationInfoSubmitParams => ({
  final_destination: params.finalDestination,
  purpose: params.purpose,
  internal_number: params.internalNumber,
  charging_department_id: params.chargingDepartmentId,
  project_id: params.projectId,
  organization_address_id: params.organizationAddressId,
  shinkansen_address_type: params.shinkansenAddressType,
  postcode: params.postcode,
  shinkansen_address: params.shinkansenAddress,
  shinkansen_addressee: params.shinkansenAddressee,
  preferred_ticketing_method: params.preferredTicketingMethod,
  traveler: params.traveler
    ? convertTravelerToSubmitParams(params.traveler, params.foreignFlightTraveler ?? false)
    : undefined
});

export const saveReservationInfo = async (
  tripId: string,
  params: SaveReservationInfoParams
): Promise<SaveReservationInfoResponse> => {
  const submitParams = convertToSubmitParams(params);
  return Fetcher.patch<SaveReservationInfoResponse>(`/trips/${tripId}/ai_reservation_info`, submitParams);
};
